// Display formatting helpers — ported from design's components.jsx.
// CRITICAL: amounts in production are bigint amount_minor (INR paise).
// Convert to major units (rupees) only at the display boundary, here.

export function ccySym(c: string): string {
  if (c === "USD") return "$";
  if (c === "INR") return "₹";
  return "";
}

/**
 * fmtCcy formats a number for display. INR uses 0 decimals, USD uses 2.
 * Returns "—" for null/undefined.
 */
export function fmtCcy(
  n: number | null | undefined,
  ccy: string,
): string {
  if (n == null) return "—";
  const fixed = ccy === "INR" ? 0 : 2;
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  const v = Math.abs(n).toLocaleString(locale, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  });
  return (n < 0 ? "−" : "") + v;
}

/**
 * fmtCcyMinor converts a bigint amount_minor (INR paise) to a display string.
 * Uses integer bigint arithmetic only — no float coercion (CLAUDE.md rule 6).
 * The Number() conversion is display-only and never feeds money math.
 *
 * Note: fmtCcy() is for major-unit amounts only (mock data / non-paise fields).
 * Use fmtCcyMinor() for all real bigint amount_minor values from the API.
 */
export function fmtCcyMinor(amountMinor: bigint, ccy: string): string {
  const neg = amountMinor < 0n;
  const abs = neg ? -amountMinor : amountMinor;
  const major = abs / 100n;
  const minor = abs % 100n;

  // Paise are shown whenever they are non-zero, and hidden when they are.
  //
  // This used to route through fmtCcy(), which renders INR with
  // maximumFractionDigits: 0 — so ₹1704.50 displayed as "₹1,705" and every
  // amount in the panel was silently rounded. That is wrong in a payouts
  // console on its own, and it actively hid a bug: a payout failed BECAUSE it
  // carried fractional paise, and the one screen that could have shown that
  // rounded the evidence away before anyone could read it.
  //
  // Always printing ".00" would be honest too, but with whole rupees now
  // enforced end-to-end it would put two dead characters on every amount on
  // every screen. Showing them only when they exist keeps the common case
  // clean and makes a fractional amount stand out — which is exactly what it
  // should do, because it should not exist.
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  const sign = neg ? "−" : "";
  const grouped = major.toLocaleString(locale);
  if (minor === 0n && ccy === "INR") {
    return `${sign}${grouped}`;
  }
  return `${sign}${grouped}.${minor.toString().padStart(2, "0")}`;
}

// Date locale — audit-pass-5 UI #19. Business context is Indian (INR,
// domestic rails, Indian merchants); rest of the app already uses en-IN for
// currency. Locking to en-IN here makes the day-month order match the
// rest of the operator's tools (23 May, not May 23) rather than US convention.
const DATE_LOCALE = "en-IN";

export function formatTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(DATE_LOCALE, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return d.toLocaleDateString(DATE_LOCALE, { month: "short", day: "numeric" });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(DATE_LOCALE, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * formatDateTimeFull is formatDateTime WITH the year — "9 Jun 2026, 14:58".
 *
 * Used where a timestamp has to stand on its own: a payout row read months
 * later, in a list that can be date-filtered to any window, gives the reader
 * no surrounding context to infer the year from. formatDateTime stays
 * year-less for dense tables where the rows are obviously recent.
 */
export function formatDateTimeFull(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(DATE_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * formatDay renders just the calendar day — "9 Jun 2026".
 *
 * The YEAR is deliberately included, unlike formatDateTime. That helper
 * feeds a per-row "Created" column the operator reads in the context of a
 * list they just filtered, where the year is usually obvious. This one
 * labels a whole batch in a history that scrolls back indefinitely, and
 * "9 Jun" alone cannot distinguish this June from the last one — which is
 * exactly the ambiguity the batch headers had.
 */
export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(DATE_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * formatDaySpan labels the period a set of rows covers, given its
 * earliest and latest timestamps.
 *
 *   same day     → "9 Jun 2026, 14:58"   (day + start time)
 *   spanning     → "9 Jun 2026 – 11 Jun 2026"
 *
 * Same-day keeps the clock time because a merchant can upload several
 * batches in one day, and the time is what tells them apart. Once the
 * rows straddle midnight the start time stops describing the group, so
 * both ends are shown instead.
 *
 * "Same day" is evaluated in the VIEWER's local timezone, matching what
 * formatDateTime prints in the rows underneath. Deciding it in UTC would
 * let a header claim one day while every row it contains shows another.
 */
export function formatDaySpan(firstIso: string, lastIso: string): string {
  const first = new Date(firstIso);
  const last = new Date(lastIso);
  if (first.toDateString() === last.toDateString()) {
    const time = first.toLocaleTimeString(DATE_LOCALE, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${formatDay(firstIso)}, ${time}`;
  }
  return `${formatDay(firstIso)} – ${formatDay(lastIso)}`;
}

/**
 * maskAccount renders a bank account number for display: keep the last
 * four digits, mask everything else, group right-to-left in fours.
 *
 *   "1234567890"  → "•• •••• 7890"
 *   "12345678901" → "••• •••• 7890"  (account length preserved)
 *   "1234"        → "1234"            (≤4 chars: nothing to mask)
 *   ""            → "—"
 *
 * The operator usually needs JUST enough of the number to disambiguate two
 * rails at a glance, without the full PII spilling into screenshots or
 * screen-shares. Same visual length as the source string so spacing in the
 * column stays predictable.
 */
export function maskAccount(acct: string | null | undefined): string {
  if (acct == null || acct === "") return "—";
  const s = acct.trim();
  if (s.length <= 4) return s;
  const masked = "•".repeat(s.length - 4) + s.slice(-4);
  // Group into 4s right-to-left, like card-number formatting.
  const groups: string[] = [];
  for (let i = masked.length; i > 0; i -= 4) {
    groups.unshift(masked.slice(Math.max(0, i - 4), i));
  }
  return groups.join(" ");
}

/**
 * fmtCcyMinorExact renders paise EXACTLY, always two decimals.
 *
 * fmtCcy deliberately drops the paise for INR — sensible on a dashboard, where
 * ₹1,23,456 reads better than ₹1,23,456.00 and nobody is transcribing it.
 *
 * It is wrong wherever the number on screen has to equal the number in a file
 * a human hands to a bank. A manual payout file carries 1760.49; showing the
 * operator "1,760" beside it invites exactly the reconciliation argument the
 * screen exists to prevent, and hides a 49-paise difference that is real money
 * to whoever is owed it.
 *
 * Integer arithmetic only, like fmtCcyMinor — the paise are formatted as a
 * two-digit remainder, never via a float.
 */
export function fmtCcyMinorExact(amountMinor: bigint, ccy = "INR"): string {
  const neg = amountMinor < 0n;
  const abs = neg ? -amountMinor : amountMinor;
  const major = abs / 100n;
  const minor = abs % 100n;
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  const grouped = major.toLocaleString(locale);
  return `${neg ? "−" : ""}${grouped}.${minor.toString().padStart(2, "0")}`;
}

/**
 * fmtMoney — the one format for a reported money figure: currency symbol,
 * grouped digits, always two decimals ("₹13,79,414.00", "₹37,734.50").
 *
 * Used for totals and tiles, where several amounts sit side by side and
 * must read as one kind of number. fmtCcyMinor (paise only when non-zero,
 * no symbol) stays for row cells, where it is deliberate.
 */
export function fmtMoney(amountMinor: bigint, ccy = "INR"): string {
  const neg = amountMinor < 0n;
  const body = fmtCcyMinorExact(neg ? -amountMinor : amountMinor, ccy);
  return `${neg ? "−" : ""}${ccySym(ccy)}${body}`;
}
